import * as THREE from 'three';
import { findClosestPointOnPoints } from '../utils';

/**
 * Converts an array of points to an array of THREE.Vector3 objects.
 * If the points are not already instances of THREE.Vector3, they will be converted.
 * @param {Array} points - The array of points to convert.
 * @returns {Array} - The array of converted points as THREE.Vector3 objects.
 */
export const convertToVector3 = (points) => {
  if (points[0] instanceof THREE.Vector3 === false) {
    points = points.map((point) => new THREE.Vector3(point.x, point.y, point.z));
  }

  return points;
}

/**
 * Calculates the center point of an array of points.
 *
 * @param {Array<THREE.Vector3>} points - The array of points.
 * @returns {THREE.Vector3} - The center point.
 */
export const calcCenter = (points) => {
  var center = new THREE.Vector3();

  points = convertToVector3(points);

  for(var i=0; i<points.length; i++){
    center.add(points[i]);
  }
  center.divideScalar(points.length);

  return center;
}

/**
 * Scales a set of 3D points around their center.
 * @param {THREE.Vector3[]} points - The array of 3D points to be scaled.
 * @param {number} scale - The scaling factor.
 * @returns {THREE.Vector3[]} The scaled 3D points.
 */
export const scale3Dpoints = (points, scale) => {
  const bBox = new THREE.Box3().setFromPoints(points);
  const center = bBox.getCenter(new THREE.Vector3());

  const scaledPoitns = points.map(p => {
    const v = p.clone().sub(center).multiplyScalar(scale).add(center);
    return v;
  });

  return scaledPoitns;
}


/**
 * Converts an array of points from non-indexed format to indexed format.
 * Each point in the input array is represented by three consecutive elements: x, y, and z coordinates.
 * Returns a new array of THREE.Vector3 objects representing the points in indexed format.
 *
 * @param {number[]} points - The array of points in non-indexed format.
 * @returns {THREE.Vector3[]} - The array of points in indexed format.
 */
export const convertPointsFromNonIndexToIndex = (points) => {
  const newPoints = [];
  for (let i = 0; i < points.length; i+=3) {
    newPoints.push(new THREE.Vector3(points[i], points[i+1], points[i+2]));
  }

  return newPoints;
}

/**
 * Offset the given points based on the normal of the closest point on the geometry.
 * @param {THREE.Vector3[]} points - The points to offset.
 * @param {THREE.BufferGeometry} geometry - The geometry to find the closest point and normal from.
 * @param {number} offset - The offset value to apply.
 * @returns {THREE.Vector3[]} The offset points.
 */
export const offsetPointsWithBufferGeometryNormal = (points, geometry, offset) => {
  // get offset points with normal of closest point of geometry
  const targetGeometry = geometry.clone();
  let targetPoints = targetGeometry.attributes.position.array;
  let targetNormals = targetGeometry.attributes.normal.array;
  if (targetPoints[0] instanceof THREE.Vector3 == false) {
    targetPoints = convertPointsFromNonIndexToIndex(targetPoints);
    targetNormals = convertPointsFromNonIndexToIndex(targetNormals);
  }
  
  const offsetPoints = points.map(p => {
    const closest = findClosestPointOnPoints(targetPoints, p);
    const normal = targetNormals[closest.idx];

    return new THREE.Vector3(p.x + normal.x * offset, p.y + normal.y * offset, p.z + normal.z * offset);
  });

  return offsetPoints;
}
